import React, { useMemo, useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import {
  Badge,
  Card,
  Collapsible,
  Icon,
  Layout,
  Page,
  TextContainer,
} from "@shopify/polaris"
import { ChevronDownMinor, ChevronUpMinor } from "@shopify/polaris-icons"
import ReactMarkdown from "react-markdown"
import withAuthRequired from "components/auth/withAuthRequired"
import i18n from "helpers/i18nHelper"

const markdownComponents = {
  h1: ({ children }) => (
    <p style={{ fontSize: "2.25rem", fontWeight: 600, marginBottom: "0.5rem" }}>
      {children}
    </p>
  ),
  h2: ({ children }) => (
    <p style={{ fontSize: "2rem", fontWeight: 600, marginBottom: "0.5rem" }}>
      {children}
    </p>
  ),
  h3: ({ children }) => (
    <p
      style={{ fontSize: "1.75rem", fontWeight: 600, marginBottom: "0.25rem" }}
    >
      {children}
    </p>
  ),
}

// Load all markdown files from the release_notes folder
const mdContext = require.context("assets/release_notes", false, /\.md$/)

const parseFrontmatter = (rawContent) => {
  const match = rawContent.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/)
  if (!match) return { meta: {}, content: rawContent }

  const meta = {}
  match[1].split("\n").forEach((line) => {
    const [key, ...rest] = line.split(":")
    if (key && rest.length) {
      meta[key.trim()] = rest.join(":").trim().replace(/^"|"$/g, "")
    }
  })

  return { meta, content: match[2].trim() }
}

const loadReleaseNotes = (lang) => {
  const langPattern = new RegExp(`\\.${lang}\\.md$`)
  const frPattern = /\.fr\.md$/

  // Collect all available files grouped by base name (date + version)
  const allKeys = mdContext.keys()
  const byBase = {}

  allKeys.forEach((key) => {
    const base = key.replace(/\.(fr|en|[a-z]{2})\.md$/, "")
    if (!byBase[base]) byBase[base] = {}
    const langMatch = key.match(/\.([a-z]{2})\.md$/)
    if (langMatch) byBase[base][langMatch[1]] = key
  })

  return Object.entries(byBase)
    .map(([base, langs]) => {
      // Use requested language, fallback to French
      const fileKey = langs[lang] || langs["fr"]
      if (!fileKey) return null

      const raw = mdContext(fileKey).default || mdContext(fileKey)
      const { meta, content } = parseFrontmatter(raw)

      // Extract sort key from filename: YYYY-MM-DD
      const dateMatch = base.match(/(\d{4}-\d{2}-\d{2})/)
      const sortKey = dateMatch ? dateMatch[1] : "0000-00-00"

      return { ...meta, content, sortKey, isFallback: !langs[lang] }
    })
    .filter(Boolean)
    .sort((a, b) => b.sortKey.localeCompare(a.sortKey))
}

const ChangelogPage = () => {
  const { t } = useTranslation("release_notes")
  const lang = i18n.language?.split("-")[0] || "fr"

  const entries = useMemo(() => loadReleaseNotes(lang), [lang])

  // Only the latest version is open by default
  const [openVersions, setOpenVersions] = useState(
    () => new Set(entries.length ? [entries[0].version] : [])
  )

  const toggle = useCallback((version) => {
    setOpenVersions((prev) => {
      const next = new Set(prev)
      next.has(version) ? next.delete(version) : next.add(version)
      return next
    })
  }, [])

  return (
    <Page title={t("title")}>
      <Layout>
        {entries.map((entry, index) => {
          const isOpen = openVersions.has(entry.version)
          const isLatest = index === 0
          return (
            <Layout.Section key={`${entry.version}-${entry.date}`}>
              <Card>
                <div
                  onClick={() => toggle(entry.version)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    userSelect: "none",
                    padding: "2rem",
                  }}
                >
                  <span>
                    <Badge status={isLatest ? "success" : "info"}>
                      {t("version", { version: entry.version })}
                    </Badge>
                    &nbsp;&nbsp;
                    <span
                      style={{
                        color: "var(--p-text-subdued)",
                        fontSize: "0.875rem",
                      }}
                    >
                      {t("released_on", { date: entry.date })}
                    </span>
                  </span>
                  <div>
                    <Icon
                      source={isOpen ? ChevronUpMinor : ChevronDownMinor}
                      color="subdued"
                    />
                  </div>
                </div>
                <Collapsible open={isOpen} id={`changelog-${entry.version}`}>
                  <Card.Section>
                    <TextContainer>
                      <ReactMarkdown components={markdownComponents}>
                        {entry.content}
                      </ReactMarkdown>
                    </TextContainer>
                  </Card.Section>
                </Collapsible>
              </Card>
            </Layout.Section>
          )
        })}
      </Layout>
    </Page>
  )
}

export default withAuthRequired(ChangelogPage)
